/**
 * Selection state for the T4-135 ambiguous-match review, kept free of React so the rules that
 * decide what gets linked are testable on their own.
 *
 * Keyed by email, which is also how the backend groups: a Xero customer has one primary email and
 * a client's primary contact has one, so an email identifies a group uniquely.
 *
 * Nothing is pre-selected anywhere in here, deliberately. Pre-selecting would let a user confirm
 * by reflex a link nobody chose, and the candidate order is ORDER BY client.id - an artefact, not
 * a signal about which client is right. The backend re-checks every pairing against fresh data
 * regardless; what is here is the explanation, not the guard.
 */
export const groupKey = group => group.email;

/**
 * Whether a group still needs the user.
 *
 * Every client in a group is Xero-unlinked - AC1 and AC2 both ask for unlinked clients, so the
 * backend filters the rest out - which means every option shown is genuinely selectable and
 * every group is genuinely confirmable.
 */
export const isGroupResolved = (group, selection) => {
	const chosen = selection[groupKey(group)];

	if (!chosen) return false;

	if (group.kind === 'MANY_CLIENTS') return typeof chosen.clientId === 'number';
	if (group.kind === 'MANY_CONTACTS') return typeof chosen.xeroContactId === 'string';

	// MANY_TO_MANY: every client needs a decision, and "do not link this client" (null) is one.
	const byClientId = chosen.byClientId || {};
	return group.clients.every(client => byClientId[client.clientId] !== undefined);
};

export const confirmedCount = (groups, selection) => groups.filter(group => isGroupResolved(group, selection)).length;

/** The name of the other client in this group already holding this contact, or null. */
export const contactTakenBy = (group, selection, xeroContactId, forClientId) => {
	const byClientId = (selection[groupKey(group)] || {}).byClientId || {};

	const holder = group.clients.find(
		client => client.clientId !== forClientId && byClientId[client.clientId] === xeroContactId
	);

	return holder ? holder.name : null;
};

/**
 * The confirmed pairings, in the shape the mutation carries.
 *
 * Only links are emitted. "Do not link this client", an unchosen customer in a MANY_CONTACTS
 * group and an unresolved group all mean the same thing to the backend - absent, which it reads
 * as IGNORE - so there is no declined state here to encode and no branch to get wrong.
 */
export const toResolutions = (groups, selection) =>
	groups.flatMap(group => {
		const chosen = selection[groupKey(group)];

		if (!chosen || !isGroupResolved(group, selection)) return [];

		if (group.kind === 'MANY_CLIENTS') {
			return [{xeroContactId: group.xeroContacts[0].xeroContactId, clientId: chosen.clientId}];
		}

		if (group.kind === 'MANY_CONTACTS') {
			return [{xeroContactId: chosen.xeroContactId, clientId: group.clients[0].clientId}];
		}

		return group.clients.flatMap(client => {
			const xeroContactId = (chosen.byClientId || {})[client.clientId];

			return xeroContactId ? [{xeroContactId, clientId: client.clientId}] : [];
		});
	});

/**
 * How many contacts the import will actually act on (T4-135).
 *
 * Not the number ticked in step one: a group where several Xero contacts compete for one client
 * links the chosen one and skips the rest, and the card says so in as many words ("the others are
 * skipped and not imported"). Counting the selection would have the button contradict the card
 * directly above it.
 *
 * So: the ones that link without asking, the ones that become new clients, and the pairings the
 * user has confirmed so far. It rises as they resolve, which also makes the effect of each choice
 * visible.
 */
export const importCount = (groups, selection, autoLinkCount, createCount) =>
	(autoLinkCount || 0) + (createCount || 0) + toResolutions(groups, selection).length;
